import { alpha, lighten, darken } from '@mui/material';
import { BOOSTER_PALETTE } from './palette';

/** Luminancia relativa (WCAG) de un hex → para decidir texto claro/oscuro sobre el acento. */
function relLuminance(hex: string): number {
  const c = hex.replace('#', '');
  const full = c.length === 3 ? c.split('').map((x) => x + x).join('') : c;
  const ch = (i: number) => parseInt(full.slice(i, i + 2), 16) / 255;
  const lin = (v: number) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));
  return 0.2126 * lin(ch(0)) + 0.7152 * lin(ch(2)) + 0.0722 * lin(ch(4));
}

const HEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/** Contraste WCAG entre dos colores (1 = idénticos, 21 = negro sobre blanco). */
function contraste(a: string, b: string): number {
  const [alto, bajo] = [relLuminance(a), relLuminance(b)].sort((x, y) => y - x);
  return (alto + 0.05) / (bajo + 0.05);
}

const TEXTO_OSCURO = '#1A1A1A';
const TEXTO_CLARO = '#FFFFFF';

/**
 * El texto que SE LEE sobre el acento — se elige midiendo, no por umbral.
 *
 * (25/08/2026) Antes era `luminancia > 0.5 ? oscuro : claro`, y ese 0.5 falla justo en los
 * tonos MEDIOS, que son la mitad de los colores que elige un tenant. El celeste de Booster
 * lo destapó: luminancia 0.376, o sea "oscuro" para el umbral → texto BLANCO a 2.46:1,
 * ilegible. Con el negro da 7.07:1. Es el texto de todos los botones primarios.
 *
 * Medir los dos y quedarse con el que gana no tiene ningún caso peor: donde el umbral
 * acertaba, el ganador es el mismo color que ya elegía.
 */
function textoSobre(color: string): string {
  return contraste(color, TEXTO_OSCURO) >= contraste(color, TEXTO_CLARO) ? TEXTO_OSCURO : TEXTO_CLARO;
}

/**
 * White-label: deriva una paleta COMPLETA a partir de UN color de acento del tenant
 * (override de la marca base). Todo lo que lee la marca (tema, variables CSS, imports
 * directos de la paleta) ve el color del tenant. Si el color es inválido, no toca nada.
 */
export function withAccent<P extends typeof BOOSTER_PALETTE>(base: P, color?: string): P {
  if (!color || !HEX.test(color)) return base;
  try {
    const onAccent = textoSobre(color);
    const light = lighten(color, 0.28);
    const mid   = darken(color, 0.12);
    const dark  = darken(color, 0.28);
    const tint  = darken(color, 0.62); // tinte muy oscuro (panel del login)
    return {
      ...base,
      brand: {
        ...base.brand,
        golden:       color,
        goldenLight:  light,
        goldenMid:    mid,
        goldenDark:   dark,
        goldenSubtle: alpha(color, 0.10),
        goldenBorder: alpha(color, 0.25),
        goldenGlow:   alpha(color, 0.18),
        loginTint:    tint,
        onAccent,
        accentCyan:   lighten(color, 0.18), // acento secundario = más claro del mismo color
      },
      gradients: {
        ...base.gradients,
        golden:     `linear-gradient(135deg, ${color} 0%, ${mid} 100%)`,
        goldenSoft: `linear-gradient(135deg, ${light} 0%, ${color} 100%)`,
        darkGolden: `linear-gradient(135deg, ${mid} 0%, ${dark} 100%)`,
        loginBg:    `linear-gradient(160deg, ${tint} 0%, #0A0A0A 60%, ${tint} 100%)`,
        cardSheen:  `linear-gradient(135deg, ${alpha(color, 0.05)} 0%, transparent 60%)`,
      },
      border: { ...base.border, golden: alpha(color, 0.28) },
      shadow: { ...base.shadow, glow: `0 0 20px ${alpha(color, 0.16)}` },
    };
  } catch {
    return base;
  }
}
